import { IVideoWithThumbnail } from '@engineering11/files-web'
import { AtLeast } from '@engineering11/types'

export enum VirtualDialogueStatus {
  Draft,
  Published_Employer,
  Published_Candidate,
  New,
}

// First, second, third. Needed to allow for specific ordering depending on status.
export const statusOrderEmployer = Object.freeze({
  [VirtualDialogueStatus.Published_Candidate]: 1,
  [VirtualDialogueStatus.Draft]: 2,
  [VirtualDialogueStatus.Published_Employer]: 3,
  [VirtualDialogueStatus.New]: 4,
})

export enum VirtualDialogueSource {
  HUMAN = 'Human',
  AUTOMATION = 'Automation',
  AI = 'AI',
}

export const AllowedSessionGrades = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D+', 'D', 'D-', 'F'] as const
export type SessionGrade = (typeof AllowedSessionGrades)[number]
export type BaseGrade = 'A' | 'B' | 'C' | 'D' | 'F'

export const GradeScores = AllowedSessionGrades.reduce((previous, current, idx) => {
  previous[current] = idx
  return previous
}, {} as Record<SessionGrade, number>)

export const GroupedSessionGrades = AllowedSessionGrades.reduce((acc, grade) => {
  const base = grade[0] as BaseGrade
  acc[base] ??= []
  acc[base].push(grade)
  return acc
}, {} as Record<BaseGrade, SessionGrade[]>)

export interface IVirtualDialogue {
  id: string
  name: string | null
  candidateId: string
  jobPostId: string
  customerKey: string
  source?: VirtualDialogueSource
  edited_by_human?: boolean
  status: VirtualDialogueStatus
  senderId: string
  senderInformation: { displayName: string }
  questions: IQuestion[]
  candidateApplicationId: string
  hasEmployerViewedResponse?: boolean
  __createdAt: string
  dateSentToCandidate: Date | null
  dateFinishedByCandidate: Date | null
  sessionGrade?: Record<string, SessionGrade>
  averageSessionGrade?: SessionGrade
  /** A list of userId of people who have display names listed in this interview session */
  involvedUsers?: string[]
  /** Display names of people involved in the dialogue, to be used for display on the FE */
  participantInformation?: Record<string, string>
}

export interface VirtualDialogueUpdateModel {
  name?: string
  hasEmployerViewedResponse?: boolean
  status?: VirtualDialogueStatus
  edited_by_human?: boolean
}

export interface VirtualDialogueGradeModel {
  sessionGrade: SessionGrade
}

export interface VirtualDialogueCreateModel {
  candidateId: string
  jobPostId: string
  name?: string
}

export interface VirtualDialoguesByCandidateAndJobRequest {
  customerKey: string
  candidateId: string
  jobPostId: string
}

export interface VirtualDialogueUpdateRequest {
  virtualDialogueId: string
  updateModel: VirtualDialogueUpdateModel
}

export interface VirtualDialogueEditQuestionRequest {
  questionId: string
  model: EditQuestionModel
}

export enum QuestionType {
  Essay = 'Essay',
  Video = 'Video',
  EssayOrVideo = 'EssayOrVideo',
}

export interface IInputQuestionTypeOption {
  type: QuestionType
  label: string
  icon: string
}

export const inputQuestionTypeOptions: IInputQuestionTypeOption[] = [
  { type: QuestionType.Essay, label: 'Text', icon: 'article' },
  { type: QuestionType.Video, label: 'Video', icon: 'video_camera_front' },
  { type: QuestionType.EssayOrVideo, label: "Candidate's choice", icon: 'account_circle' },
]

export const dialogueQuestionTypeMappings = new Map<QuestionType, string>()
  .set(QuestionType.Essay, 'Text')
  .set(QuestionType.Video, 'Video')
  .set(QuestionType.EssayOrVideo, 'Text or Video')

export enum ThumbRating {
  ThumbsUp = 'ThumbsUp',
  ThumbsDown = 'ThumbsDown',
}
export interface BaseQuestion {
  id: string
  position: number
  question: string
  type: QuestionType
  libraryId?: string | null
  /** User Id to their thumb rating. Should only appear on the employer side of the the interview. */
  reactions?: Record<string, ThumbRating | null>
}

export interface EssayQuestion extends BaseQuestion {
  type: QuestionType.Essay
  response: string | null
}

export interface VideoQuestion extends BaseQuestion {
  type: QuestionType.Video
  response: IVideoWithThumbnail | null
}

export interface EssayOrVideoQuestion extends BaseQuestion {
  type: QuestionType.EssayOrVideo
  response: string | IVideoWithThumbnail | null
}

export type IQuestion = EssayQuestion | VideoQuestion | EssayOrVideoQuestion
export type IQuestionResponse = IQuestion['response']

export type IQuestionUpdate = AtLeast<IQuestion, 'id'>

export type CreateQuestionModel = Pick<BaseQuestion, 'type' | 'question' | 'libraryId'>

export interface ICreateQuestionModelWithExtra {
  model: CreateQuestionModel
  saveToLibrary?: boolean
}

export type EditQuestionModel = Partial<Pick<BaseQuestion, 'position' | 'question' | 'type' | 'libraryId'>>

export interface ReactToQuestion {
  thumbRating: ThumbRating | null
}

export interface DeleteQuestionRequest {
  questionId: string
  libraryId?: string
}
